import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Activity, Globe, Clock, Stethoscope, Bed, HeartPulse, Play } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ContactModal } from '../components/ui/ContactModal';
import { HeroCarousel } from '../components/ui/HeroCarousel';
import { VideoModal } from '../components/ui/VideoModal';
import { useLanguage } from '../context/LanguageContext';
export function HomePage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState<{
    videoId: string;
    caption: string;
  } | null>(null);
  const {
    t
  } = useLanguage();
  const specialties = [{
    name: t.specialties.items.general.name,
    icon: Activity
  }, {
    name: t.specialties.items.laparoscopic.name,
    icon: Stethoscope
  }, {
    name: t.specialties.items.urological.name,
    icon: Activity
  }, {
    name: t.specialties.items.colorectal.name,
    icon: Activity
  }, {
    name: t.specialties.items.oncological.name,
    icon: Activity
  }, {
    name: t.specialties.items.orthopedic.name,
    icon: Activity
  }, {
    name: t.specialties.items.ent.name,
    icon: Activity
  }];
  return <div className="flex flex-col" data-id="element-496">
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-497" />

      {/* Hero Carousel */}
      <HeroCarousel data-id="element-498" />

      {/* Stats Bar */}
      <section className="bg-white py-10 border-b border-gray-100" data-id="element-499">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-500">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-501">
            {[{
            label: t.home.stats.rooms,
            value: '7',
            icon: Bed
          }, {
            label: t.home.stats.operation,
            value: '24/7',
            icon: Clock
          }, {
            label: t.home.stats.specialties,
            value: '7+',
            icon: Stethoscope
          }, {
            label: t.home.stats.reach,
            value: 'Global',
            icon: Globe
          }].map((stat, i) => <div key={i} className="flex items-center justify-center md:justify-start gap-4" data-id="element-502">
                <div className="p-3 bg-caces-sky rounded-full text-caces-navy" data-id="element-503">
                  <stat.icon size={24} data-id="element-504" />
                </div>
                <div data-id="element-505">
                  <p className="text-2xl font-bold text-caces-navy" data-id="element-506">
                    {stat.value}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-507">{stat.label}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-gray-50" data-id="element-508">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-509">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-510">
            <div data-id="element-511">
              <SectionHeading title={t.home.about.title} subtitle={t.home.about.subtitle} align="left" data-id="element-512" />
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-513">
                {t.home.about.p1}
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-514">
                {t.home.about.p2}
              </p>
              <Link to="/about" data-id="element-515">
                <Button variant="outline" data-id="element-516">{t.home.about.btn}</Button>
              </Link>
            </div>
            <div className="relative" data-id="element-517">
              <div className="absolute -inset-4 bg-caces-accent/10 rounded-2xl transform rotate-3" data-id="element-518"></div>
              <div className="relative bg-white p-2 rounded-xl shadow-lg" data-id="element-519">
                <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden" data-id="element-520">
                  <div className="text-gray-400 flex flex-col items-center" data-id="element-521">
                    <Activity size={48} className="mb-2" data-id="element-522" />
                    <span data-id="element-523">{t.home.about.imgLabel}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Highlight */}
      <section className="py-20 bg-white" data-id="element-524">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-525">
          <SectionHeading title={t.home.facilities.title} subtitle={t.home.facilities.subtitle} data-id="element-526" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12" data-id="element-527">
            {[{
            title: t.home.facilities.or.title,
            desc: t.home.facilities.or.desc,
            icon: Activity
          }, {
            title: t.home.facilities.recovery.title,
            desc: t.home.facilities.recovery.desc,
            icon: HeartPulse
          }, {
            title: t.home.facilities.suites.title,
            desc: t.home.facilities.suites.desc,
            icon: Bed
          }].map((item, i) => <Card key={i} hoverEffect className="p-8 text-center group" data-id="element-528">
                <div className="w-16 h-16 bg-caces-sky rounded-full flex items-center justify-center mx-auto mb-6 text-caces-navy group-hover:bg-caces-navy group-hover:text-white transition-colors" data-id="element-529">
                  <item.icon size={32} data-id="element-530" />
                </div>
                <h3 className="text-xl font-serif font-bold text-caces-navy mb-3" data-id="element-531">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-532">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Institutional Video Section */}
      <section className="py-20 bg-gray-50" data-id="element-533">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-534">
          <SectionHeading title={t.home.videos.title} subtitle={t.home.videos.subtitle} data-id="element-535" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12" data-id="element-536">
            {[{
            thumbnail: 'https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=640&q=80',
            caption: t.home.videos.items[0].caption,
            videoId: t.home.videos.items[0].videoId
          }, {
            thumbnail: 'https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?w=640&q=80',
            caption: t.home.videos.items[1].caption,
            videoId: t.home.videos.items[1].videoId
          }, {
            thumbnail: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=640&q=80',
            caption: t.home.videos.items[2].caption,
            videoId: t.home.videos.items[2].videoId
          }].map((video, i) => <button key={i} onClick={() => setActiveVideo({
            videoId: video.videoId,
            caption: video.caption
          })} className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-caces-accent focus-visible:ring-offset-2 rounded-xl" aria-label={`Play video: ${video.caption}`} data-id="element-537">
                <div className="relative aspect-video rounded-xl overflow-hidden shadow-md" data-id="element-538">
                  <img src={video.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-539" />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-caces-navy/40 group-hover:bg-caces-navy/30 transition-colors" data-id="element-540" />
                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-541">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-542">
                      <Play size={28} className="text-caces-navy ml-1" fill="currentColor" data-id="element-543" />
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm font-medium text-gray-700 text-center" data-id="element-544">
                  {video.caption}
                </p>
              </button>)}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && <VideoModal isOpen={!!activeVideo} onClose={() => setActiveVideo(null)} videoId={activeVideo.videoId} caption={activeVideo.caption} data-id="element-545" />}

      {/* Specialties Preview */}
      <section className="py-20 bg-caces-navy text-white" data-id="element-546">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-547">
          <SectionHeading title={t.home.specialties.title} subtitle={t.home.specialties.subtitle} light data-id="element-548" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-id="element-549">
            {specialties.map((spec, i) => <Link key={i} to="/specialties" className="block" data-id="element-550">
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors text-center h-full flex flex-col items-center justify-center" data-id="element-551">
                  <spec.icon className="w-8 h-8 text-caces-accent mb-3" data-id="element-552" />
                  <span className="font-medium text-caces-light" data-id="element-553">
                    {spec.name}
                  </span>
                </div>
              </Link>)}
            <Link to="/specialties" className="block" data-id="element-554">
              <div className="bg-caces-accent border border-caces-accent rounded-lg p-6 hover:bg-caces-blue transition-colors text-center h-full flex flex-col items-center justify-center" data-id="element-555">
                <ArrowRight className="w-8 h-8 text-white mb-3" data-id="element-556" />
                <span className="font-medium text-white" data-id="element-557">
                  {t.home.specialties.viewAll}
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-caces-sky" data-id="element-558">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-559">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-caces-navy mb-6" data-id="element-560">
            {t.home.cta.title}
          </h2>
          <p className="text-lg text-gray-600 mb-8" data-id="element-561">{t.home.cta.desc}</p>
          <Button size="lg" onClick={() => setIsModalOpen(true)} data-id="element-562">
            {t.home.cta.btn}
          </Button>
        </div>
      </section>
    </div>;
}